// "Are you the lowest price?" — what a supplier may know about where its offer stands.
//
// The rule this file exists to keep: a supplier learns whether it is the lowest offer and,
// when it is not, a coarse band. It never learns a competitor's price or name. The server
// (RPC get_provider_price_positions) only returns the verdict, so nothing here can leak more
// than it was given — do not add a field that carries an amount.

export type GapBand = 'up_to_5_pct' | '5_to_15_pct' | 'over_15_pct';

export interface VariantPricePosition {
  position: 'lowest' | 'not_lowest';
  gapBand: GapBand | null;
}

export interface ProductPricePosition {
  /** Sizes in stock where this supplier is the lowest offer (ties count). */
  lowest: number;
  /** Sizes in stock that could be ranked at all. */
  total: number;
  byVariant: Record<string, VariantPricePosition>;
}

interface PositionRow {
  product_id: string;
  variant_id: string;
  position: string;
  gap_band: string | null;
}

const BANDS: GapBand[] = ['up_to_5_pct', '5_to_15_pct', 'over_15_pct'];

/** Group the RPC's per-size rows by product. Rows with an unknown verdict are dropped. */
export function groupPositions(rows: unknown): Record<string, ProductPricePosition> {
  const out: Record<string, ProductPricePosition> = {};
  if (!Array.isArray(rows)) return out;

  for (const row of rows as PositionRow[]) {
    if (!row || !row.product_id || !row.variant_id) continue;
    if (row.position !== 'lowest' && row.position !== 'not_lowest') continue;

    const product = out[row.product_id] ?? (out[row.product_id] = { lowest: 0, total: 0, byVariant: {} });
    const gapBand = BANDS.includes(row.gap_band as GapBand) ? (row.gap_band as GapBand) : null;
    product.byVariant[row.variant_id] = {
      position: row.position,
      gapBand: row.position === 'not_lowest' ? gapBand : null,
    };
    product.total += 1;
    if (row.position === 'lowest') product.lowest += 1;
  }
  return out;
}

export function gapBandLabel(band: GapBand | null): string | null {
  switch (band) {
    case 'up_to_5_pct':
      return 'up to 5% above';
    case '5_to_15_pct':
      return '5–15% above';
    case 'over_15_pct':
      return 'more than 15% above';
    default:
      return null;
  }
}

export interface PositionLabel {
  text: string;
  tone: 'lowest' | 'mixed' | 'not_lowest';
}

/** Badge for one size. */
export function variantLabel(p: VariantPricePosition): PositionLabel {
  if (p.position === 'lowest') return { text: 'Lowest price', tone: 'lowest' };
  const band = gapBandLabel(p.gapBand);
  return { text: band ? `Not the lowest · ${band}` : 'Not the lowest', tone: 'not_lowest' };
}

/** Badge for a whole product, rolled up over its in-stock sizes. Null when nothing is in stock. */
export function productLabel(p: ProductPricePosition | undefined): PositionLabel | null {
  if (!p || p.total === 0) return null;
  if (p.lowest === p.total) return { text: 'Lowest price', tone: 'lowest' };
  if (p.lowest === 0) return { text: 'Not the lowest', tone: 'not_lowest' };
  return { text: `Lowest on ${p.lowest} of ${p.total} sizes`, tone: 'mixed' };
}
